import { useState } from "react";
import type { User, Workstream } from "../types";
import { todayLabel, uid } from "../utils";
import { EditableField } from "./EditableField";
import { EditableTable } from "./EditableTable";
import { StatusBadge } from "./StatusBadge";

interface Props {
  ws: Workstream;
  user: User | null;
  onUpdate: (next: Workstream) => void;
}

export function WorkstreamDetail({ ws, user, onUpdate }: Props) {
  const [newLog, setNewLog] = useState("");

  function set<K extends keyof Workstream>(k: K, v: Workstream[K]) {
    onUpdate({ ...ws, [k]: v } as Workstream);
  }

  function addLog() {
    if (!newLog.trim()) return;
    const entry = {
      id: uid(),
      date: todayLabel(),
      updatedBy: user?.userDetails ?? "Unknown",
      notes: newLog.trim(),
    };
    onUpdate({ ...ws, updateLog: [entry, ...(ws.updateLog ?? [])] });
    setNewLog("");
  }

  function deleteLog(id: string) {
    onUpdate({ ...ws, updateLog: (ws.updateLog ?? []).filter((e) => e.id !== id) });
  }

  return (
    <div className="space-y-5">
      {/* Meta bar */}
      <div className="bg-white rounded-2xl ring-1 ring-slate-200 px-6 py-5 flex flex-wrap gap-8 items-start shadow-sm">
        <div>
          <p className="text-xs text-slate-400 mb-1.5">Overall Status</p>
          <StatusBadge status={ws.status} lg onChange={(v) => set("status", v)} />
        </div>
        <div className="min-w-[120px]">
          <p className="text-xs text-slate-400 mb-1">Deerfield Lead</p>
          <EditableField
            value={ws.deerfieldLead}
            onChange={(v) => set("deerfieldLead", v)}
            className="font-semibold text-slate-800 text-sm"
          />
        </div>
        <div className="min-w-[120px]">
          <p className="text-xs text-slate-400 mb-1">HSS Lead</p>
          <EditableField
            value={ws.hssLead}
            onChange={(v) => set("hssLead", v)}
            className="font-semibold text-slate-800 text-sm"
          />
        </div>
        <div className="min-w-[150px]">
          <p className="text-xs text-slate-400 mb-1">Gating Factor</p>
          <EditableField
            value={ws.gatingFactor}
            onChange={(v) => set("gatingFactor", v)}
            placeholder="None"
            multi
            className="text-sm text-amber-700"
          />
        </div>
        <div className="flex-1 min-w-[180px]">
          <p className="text-xs text-slate-400 mb-1">Latest Update</p>
          <EditableField
            value={ws.notes}
            onChange={(v) => set("notes", v)}
            placeholder="Add a note..."
            multi
            className="text-sm text-slate-600"
          />
        </div>
      </div>

      {/* Body: editable table (column schema is data) */}
      <EditableTable ws={ws} onUpdate={onUpdate} />

      {/* Weekly update log (shared across all workstreams) */}
      <div className="bg-white rounded-2xl ring-1 ring-slate-200 px-6 py-5 shadow-sm">
        <p className="text-xs font-semibold text-slate-400 uppercase tracking-widest mb-4">
          Weekly Update Log
        </p>
        <div className="flex gap-2 mb-4">
          <input
            value={newLog}
            onChange={(e) => setNewLog(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addLog()}
            placeholder="Add a log entry and press Enter..."
            className="flex-1 text-sm px-3 py-2 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-400"
          />
          <button
            onClick={addLog}
            className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-white text-sm rounded-lg transition-colors"
          >
            Add
          </button>
        </div>
        {(ws.updateLog ?? []).length > 0 ? (
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-slate-100 text-slate-400 font-medium">
                <th className="text-left pb-2 w-28">Date</th>
                <th className="text-left pb-2 w-28">Updated By</th>
                <th className="text-left pb-2">Notes</th>
                <th className="w-8" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {(ws.updateLog ?? []).map((e) => (
                <tr key={e.id} className="group">
                  <td className="py-2 text-slate-400">{e.date}</td>
                  <td className="py-2 text-slate-600 font-medium">{e.updatedBy}</td>
                  <td className="py-2 text-slate-500">{e.notes}</td>
                  <td className="py-2">
                    <button
                      onClick={() => deleteLog(e.id)}
                      className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-600 transition-opacity text-sm"
                    >
                      &times;
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="text-xs text-slate-300 italic">No updates logged yet.</p>
        )}
      </div>
    </div>
  );
}
